/** Inline SVG illustrations for StepCards — built from Figma vectors, no raster images */
import { siteConfig } from "@/config/site";

const isScriptario = siteConfig.siteId === "scriptario";
const illustrationLocale = isScriptario && siteConfig.locale !== "ru" ? siteConfig.locale : "ru";

const stepCopy = {
  de: { upload: "Datei ziehen, hochladen oder aufnehmen", readyTitle: "Interview für ein Magazin", readyStatus: "Transkript ist fertig!", progressTitle: "Strategiebesprechung des Teams", speaker: "Daniel", transcript: <>Ich schlage eine <span style={{ color: "rgba(0,0,0,0.9)", fontWeight: 600 }}>neue</span> Kennzahl vor. Vergessen wir MQL und SQL und messen wir die Geschwindigkeit des Vertrauens...</> },
  en: {
    upload: "Drag, upload a file, or record",
    readyTitle: "Magazine interview",
    readyStatus: "Transcript is ready!",
    progressTitle: "Team strategy session",
    speaker: "Daniel",
    transcript: <>I suggest a <span style={{ color: "rgba(0,0,0,0.9)", fontWeight: 600 }}>new</span> metric. Forget MQL and SQL. Let us measure trust velocity...</>,
  },
  es: {
    upload: "Arrastra, sube un archivo o graba",
    readyTitle: "Entrevista para una revista",
    readyStatus: "Transcripción lista",
    progressTitle: "Sesión estratégica del equipo",
    speaker: "Daniel",
    transcript: <>Propongo una <span style={{ color: "rgba(0,0,0,0.9)", fontWeight: 600 }}>nueva</span> métrica. Olvidemos MQL y SQL. Midamos la velocidad de confianza...</>,
  },
  "pt-br": {
    upload: "Arraste, envie um arquivo ou grave",
    readyTitle: "Entrevista para revista",
    readyStatus: "Transcrição pronta",
    progressTitle: "Sessão estratégica da equipe",
    speaker: "Daniel",
    transcript: <>Sugiro uma <span style={{ color: "rgba(0,0,0,0.9)", fontWeight: 600 }}>nova</span> métrica. Esqueça MQL e SQL. Vamos medir a velocidade de confiança...</>,
  },
  it: {
    upload: "Trascina, carica un file o registra",
    readyTitle: "Intervista per una rivista",
    readyStatus: "Trascrizione pronta",
    progressTitle: "Sessione strategica del team",
    speaker: "Daniel",
    transcript: <>Propongo una <span style={{ color: "rgba(0,0,0,0.9)", fontWeight: 600 }}>nuova</span> metrica. Dimentichiamo MQL e SQL. Misuriamo la velocità della fiducia...</>,
  },
  ru: {
    upload: "Перетащите, загрузите файл или сделайте запись",
    readyTitle: "Интервью для журнала",
    readyStatus: "Транскрипция готова!",
    progressTitle: "Стратегическая сессия команды",
    speaker: "Сергей",
    transcript: <>Я предлагаю <span style={{ color: "rgba(0,0,0,0.9)", fontWeight: 600 }}>новую</span> метрику. Забудьте про MQL и SQL. Вводим показатель «Скорость доверия». Как его измерить? Просто...</>,
  },
} as const;

const copy = stepCopy[illustrationLocale];

/** Grab-hand cursor (Figma node 1385-7907) — Shape (hand) + Union (finger lines). */
function CursorPointer({ className = "" }: { className?: string }) {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className={`overflow-visible drop-shadow-[0px_1px_1.8px_rgba(0,0,0,0.65)] ${className}`}
    >
      {/* Shape — hand, viewBox 0 0 17.17 15.93, centred in 24×24 */}
      <g transform="translate(3.4 4)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.7451 1.95483C5.2251 1.77483 6.1751 1.88483 6.4251 2.42483C6.6751 2.96483 6.8251 3.66483 6.8351 3.49483C6.81597 2.97813 6.85962 2.46102 6.9651 1.95483C7.07625 1.63065 7.33091 1.37598 7.6551 1.26483C7.95242 1.1708 8.26809 1.15022 8.5751 1.20483C8.88551 1.26873 9.16045 1.44726 9.3451 1.70483C9.57905 2.28796 9.71097 2.90699 9.7351 3.53483C9.75997 2.99909 9.85065 2.46844 10.0051 1.95483C10.1722 1.71938 10.4162 1.54962 10.6951 1.47483C11.0257 1.41439 11.3645 1.41439 11.6951 1.47483C11.9665 1.56489 12.2038 1.73589 12.3751 1.96483C12.5875 2.49498 12.7157 3.05508 12.7551 3.62483C12.7551 3.76483 12.8251 3.23483 13.0451 2.88483C13.2218 2.36016 13.7904 2.0781 14.3151 2.25483C14.8398 2.43156 15.1218 3.00016 14.9451 3.52483C14.9451 4.17483 14.9451 4.14483 14.9451 4.58483C14.9451 5.02483 14.9451 5.41483 14.9451 5.78483C14.9091 6.37005 14.8288 6.95171 14.7051 7.52483C14.5315 8.03215 14.2893 8.51329 13.9851 8.95483C13.4997 9.49489 13.0985 10.1051 12.7951 10.7648C12.7211 11.0928 12.6875 11.4287 12.6951 11.7648C12.6941 12.0755 12.7344 12.3849 12.8151 12.6848C12.4063 12.7285 11.9939 12.7285 11.5851 12.6848C11.1951 12.6248 10.7151 11.8448 10.5851 11.6048C10.5208 11.476 10.3891 11.3945 10.2451 11.3945C10.1011 11.3945 9.96942 11.476 9.9051 11.6048C9.6851 11.9848 9.1951 12.6748 8.9051 12.7148C8.2351 12.7948 6.8451 12.7148 5.7651 12.7148C5.7651 12.7148 5.9551 11.7148 5.5351 11.3548C5.1151 10.9948 4.7051 10.5748 4.3951 10.2948L3.5651 9.37483C2.97979 8.83151 2.55148 8.14069 2.3251 7.37483C2.1151 6.43483 2.1351 5.98483 2.3251 5.60483C2.51889 5.29104 2.82149 5.05964 3.1751 4.95483C3.46887 4.90154 3.77128 4.92216 4.0551 5.01483C4.25137 5.097 4.42099 5.23201 4.5451 5.40483C4.7751 5.71483 4.8551 5.86483 4.7551 5.52483C4.6551 5.18483 4.4351 4.93483 4.3251 4.52483C4.11095 4.04063 3.98238 3.52296 3.9451 2.99483C3.96555 2.51407 4.28566 2.09791 4.7451 1.95483Z"
          fill="white"
          stroke="black"
          strokeWidth="0.75"
          strokeLinejoin="round"
        />
      </g>
      {/* Union — finger lines above the hand */}
      <g transform="translate(9.7 1.5)">
        <path
          d="M0.373053 5.24309e-06C0.580047 -0.00108331 0.748796 0.167469 0.750006 0.375982L0.769538 3.82032C0.770631 4.02872 0.604375 4.19884 0.397467 4.2002C0.190364 4.20142 0.0207488 4.03282 0.0195377 3.82422L6.40294e-06 0.379888C-0.0011919 0.171306 0.165958 0.00122504 0.373053 5.24309e-06ZM2.37305 5.24309e-06C2.58006 -0.00107019 2.74881 0.165046 2.75001 0.371099L2.76954 3.82422C2.77073 4.03025 2.60443 4.19885 2.39747 4.2002C2.19042 4.20139 2.02082 4.03515 2.01954 3.82911L2.00001 0.375005C1.99902 0.169054 2.16608 0.00119254 2.37305 5.24309e-06ZM4.37501 5.24309e-06C4.58208 5.37161e-06 4.74996 0.167453 4.75001 0.374029V3.82618C4.74985 4.03266 4.58202 4.2002 4.37501 4.2002C4.16799 4.2002 4.00016 4.03266 4.00001 3.82618V0.374029C4.00005 0.167453 4.16793 5.24309e-06 4.37501 5.24309e-06Z"
          fill="black"
        />
      </g>
    </svg>
  );
}

/** File card with dog-ear (Figma node 1385-7907). White body, colored glyph. */
function FileCard({ glyph, className = "" }: { glyph: React.ReactNode; className?: string }) {
  return (
    <svg
      width="62"
      height="76"
      viewBox="10 10 62 76"
      fill="none"
      className={`overflow-visible drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] ${className}`}
    >
      {/* Card body with folded corner */}
      <path
        d="M10 18C10 13.5817 13.5817 10 18 10H41H48.75L72 38.5V48V78C72 82.4183 68.4183 86 64 86H18C13.5817 86 10 82.4183 10 78V18Z"
        fill="white"
      />
      {/* Dog-ear */}
      <path
        d="M48.9987 10.0024L71.9987 38.0024H56.9987C52.5804 38.0024 48.9987 34.4207 48.9987 30.0024V10.0024Z"
        fill="white"
      />
      {glyph}
    </svg>
  );
}

/** Audio file — music notes (left card). */
function FileAudio({ className = "" }: { className?: string }) {
  return (
    <FileCard
      className={className}
      glyph={
        <path
          d="M51.6154 41.2165C51.4962 41.122 51.3568 41.0564 51.2081 41.0247C51.0593 40.993 50.9052 40.9962 50.7579 41.034L34.7579 45.034C34.5418 45.088 34.3499 45.2126 34.2127 45.3881C34.0754 45.5636 34.0007 45.7799 34.0004 46.0027V59.7652C33.1661 59.2078 32.1694 58.9452 31.1688 59.0191C30.1681 59.0931 29.2209 59.4993 28.4776 60.1733C27.7343 60.8474 27.2376 61.7505 27.0664 62.7391C26.8952 63.7278 27.0594 64.7453 27.5328 65.63C28.0062 66.5147 28.7617 67.2158 29.6792 67.6219C30.5968 68.028 31.6237 68.1157 32.5969 67.8713C33.57 67.6268 34.4335 67.0641 35.0502 66.2725C35.6669 65.481 36.0013 64.5061 36.0004 63.5027V51.784L50.0004 48.284V55.7652C49.1661 55.2078 48.1694 54.9452 47.1688 55.0191C46.1681 55.0931 45.2209 55.4993 44.4776 56.1733C43.7343 56.8474 43.2376 57.7505 43.0664 58.7391C42.8952 59.7278 43.0594 60.7453 43.5328 61.63C44.0062 62.5147 44.7617 63.2158 45.6792 63.6219C46.5968 64.028 47.6237 64.1157 48.5969 63.8713C49.57 63.6268 50.4335 63.0641 51.0502 62.2725C51.6669 61.481 52.0013 60.5061 52.0004 59.5027V42.0027C52.0001 41.851 51.9652 41.7014 51.8985 41.5652C51.8318 41.429 51.735 41.3097 51.6154 41.2165Z"
          fill="var(--brand-primary-light)"
        />
      }
    />
  );
}

/** Video file — play triangle (right card). */
function FileDoc({ className = "" }: { className?: string }) {
  return (
    <FileCard
      className={className}
      glyph={
        <path
          d="M55 55.0029C55.0008 55.3424 54.9137 55.6764 54.7473 55.9723C54.5808 56.2683 54.3406 56.5161 54.05 56.6917L36.04 67.7092C35.7363 67.8951 35.3885 67.9966 35.0326 68.0032C34.6766 68.0098 34.3253 67.9212 34.015 67.7467C33.7076 67.5748 33.4515 67.3242 33.2732 67.0206C33.0948 66.717 33.0005 66.3713 33 66.0192V43.9867C33.0005 43.6345 33.0948 43.2889 33.2732 42.9852C33.4515 42.6816 33.7076 42.431 34.015 42.2592C34.3253 42.0846 34.6766 41.996 35.0326 42.0026C35.3885 42.0092 35.7363 42.1107 36.04 42.2967L54.05 53.3142C54.3406 53.4897 54.5808 53.7376 54.7473 54.0335C54.9137 54.3294 55.0008 54.6634 55 55.0029Z"
          fill="var(--brand-primary-light)"
        />
      }
    />
  );
}

export function Step1Illustration() {
  return (
    <>
      {/* Mini upload card */}
      <div className="absolute left-1/2 -translate-x-1/2 top-[192px] bg-white drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] flex flex-col h-[120px] items-center justify-center p-[12px] rounded-[20px] w-[195px]">
        <div className="border border-dashed border-border-dashed flex flex-1 flex-col items-center justify-center px-[16px] py-[24px] rounded-[8px] w-full">
          <p
            className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-primary text-center w-full"
            style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}
          >
            {copy.upload}
          </p>
        </div>
      </div>

      {/* Floating file cards */}
      {/* Video file (play) — right, tilted +5.79° */}
      <div className="absolute left-[calc(50%+113px)] top-[219px] rotate-[5.79deg]">
        <FileDoc />
      </div>
      {/* Audio file (music notes) — left, tilted -3° */}
      <div className="absolute left-[calc(50%-90.72px)] top-[252px] -rotate-3">
        <FileAudio />
      </div>

      {/* Grab-hand cursors */}
      <div className="absolute left-[calc(50%+128px)] top-[274px]">
        <CursorPointer />
      </div>
      <div className="absolute left-[calc(50%-104.67px)] top-[307px]">
        <CursorPointer />
      </div>
    </>
  );
}

/** Step 2: notification cards showing "Транскрипция готова!" */
export function Step2Illustration() {
  return (
    <>
      {/* Notification card 1 — готово */}
      <div className="absolute left-[-13px] top-[190px] rotate-[-3.2deg] max-[480px]:left-[-8px]">
        <div className="bg-white border border-[rgba(18,184,41,0.8)] drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] flex flex-col gap-[8px] items-center p-[16px] rounded-[16px] w-[274px] max-[480px]:w-[248px]">
          <div className="flex items-center justify-between w-full">
            <span className="flex-1 [font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text overflow-hidden text-ellipsis whitespace-nowrap">
              {copy.readyTitle}
            </span>
            <span className="size-[16px] text-gray-400 shrink-0">✕</span>
          </div>
          <div className="flex gap-[8px] items-center justify-center w-full">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#12B829" strokeWidth="1.5" />
              <path d="M5 8l2 2 4-4" stroke="#12B829" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text whitespace-nowrap">
              {copy.readyStatus}
            </span>
          </div>
        </div>
      </div>

      {/* Notification card 2 — прогресс */}
      <div className="absolute left-[161px] top-[265px] rotate-[2.64deg] max-[480px]:left-auto max-[480px]:right-[-8px]">
        <div className="bg-white border border-border-default drop-shadow-[0px_0px_5px_rgba(0,0,0,0.1)] flex flex-col gap-[8px] items-center p-[16px] rounded-[16px] w-[274px] max-[480px]:w-[230px]">
          <div className="flex items-center justify-between w-full">
            <span className="flex-1 [font-family:var(--font-commissioner)] font-normal not-italic text-[14px] leading-[16px] text-text overflow-hidden text-ellipsis whitespace-nowrap">
              {copy.progressTitle}
            </span>
            <span className="size-[16px] text-gray-400 shrink-0">✕</span>
          </div>
          <div className="flex flex-col gap-[4px] items-center pt-[6px] w-full">
            <div className="bg-black/10 h-[6px] rounded-[8px] w-full overflow-hidden">
              <div className="bg-[var(--brand-primary-light)] h-[6px] rounded-[8px] w-[105px]" />
            </div>
            <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[12px] leading-[16px] text-text-muted tracking-[-0.24px] whitespace-nowrap">
              65%
            </span>
          </div>
        </div>
      </div>
    </>
  );
}

/** Step 3: editor window with text — Figma Frame86 */
export function Step3Illustration() {
  return (
    <div className="absolute left-[42px] top-[192px] flex flex-col gap-[8px] items-end w-[330px] max-[480px]:left-[20px] max-[480px]:right-[20px] max-[480px]:w-auto">
      <div className="flex items-start justify-between w-full">
        <div className="flex gap-[8px] items-center">
          <span className="bg-[rgba(227,134,116,0.4)] px-[4px] py-[2px] rounded-[3px] text-[12px] leading-[16px] whitespace-nowrap" style={{ fontFamily: "var(--font-commissioner)", color: "rgba(0,0,0,0.9)" }}>
            {copy.speaker}
          </span>
          <svg className="size-[16px] text-gray-400 shrink-0" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M227.31,73.37,182.63,28.68a16,16,0,0,0-22.63,0L36.69,152A15.86,15.86,0,0,0,32,163.31V208a16,16,0,0,0,16,16H92.69A15.86,15.86,0,0,0,104,219.31L227.31,96a16,16,0,0,0,0-22.63ZM92.69,208H48V163.31l88-88L180.69,120ZM192,108.68,147.31,64l24-24L216,84.68Z" /></svg>
          <svg className="size-[16px] text-gray-400 shrink-0" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M216,32H88a8,8,0,0,0-8,8V80H40a8,8,0,0,0-8,8V216a8,8,0,0,0,8,8H168a8,8,0,0,0,8-8V176h40a8,8,0,0,0,8-8V40A8,8,0,0,0,216,32ZM160,208H48V96H160Zm56-48H176V88a8,8,0,0,0-8-8H96V48H216Z" /></svg>
        </div>
        <span className="[font-family:var(--font-commissioner)] font-normal not-italic text-[16px] leading-[20px] text-text whitespace-nowrap" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
          00:00:00
        </span>
      </div>
      <p className="[font-family:var(--font-commissioner)] font-normal not-italic text-[18px] leading-[24px] text-text w-full" style={{ fontVariationSettings: '"FLAR" 0, "VOLM" 0' }}>
        {copy.transcript}
      </p>
    </div>
  );
}
